import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

// ─────────────────────────────────────────────────────────────────────────────
// ClientManagement.js
//
// Admin-only panel to:
//   1. Create / edit / delete client records in the top-level /clients collection
//   2. Assign each project to a client by stamping `clientId` onto the project doc
//
// This is what makes "projects grouped by client" work — the User Management
// panel groups projects by `clientId`, so any project without one shows as
// "Unassigned" until you set it here.
//
// Client doc shape:  /clients/{clientId}  { name, contactPerson, email, phone, notes }
// Project link:      /projects/{projectId} gains a `clientId` field
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FORM = { name: "", contactPerson: "", email: "", phone: "", notes: "" };

export default function ClientManagement({ currentUser }) {
  const [clients,  setClients]  = useState([]);
  const [projects, setProjects] = useState([]);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [editId,   setEditId]   = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  // Live listeners
  useEffect(() => {
    const u1 = onSnapshot(collection(db, "clients"), (s) =>
      setClients(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(collection(db, "projects"), (s) =>
      setProjects(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); };
  }, []);

  const patch = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // Count projects per client for the register
  const projectCountByClient = useMemo(() => {
    const counts = {};
    projects.forEach((p) => {
      if (p.clientId) counts[p.clientId] = (counts[p.clientId] || 0) + 1;
    });
    return counts;
  }, [projects]);

  const unassignedCount = useMemo(
    () => projects.filter((p) => !p.clientId).length,
    [projects]
  );

  // ── Create / update a client ────────────────────────────────────────────
  const handleSave = async () => {
    setError(""); setSuccess("");
    if (!form.name.trim()) { setError("Client name is required."); return; }

    setSaving(true);
    try {
      // For a new client, generate an id from the name (slug + short random).
      const id =
        editId ||
        form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) +
          "-" + Math.random().toString(36).slice(-4);

      await setDoc(
        doc(db, "clients", id),
        {
          name:          form.name.trim(),
          contactPerson: form.contactPerson.trim(),
          email:         form.email.trim(),
          phone:         form.phone.trim(),
          notes:         form.notes.trim(),
          updatedAt:     serverTimestamp(),
          updatedBy:     currentUser?.email || "admin",
          ...(editId ? {} : { createdAt: serverTimestamp() }),
        },
        { merge: true }
      );
      setSuccess(`Client "${form.name.trim()}" saved.`);
      setForm(EMPTY_FORM);
      setEditId(null);
    } catch (err) {
      setError(err.message || "Failed to save client.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (client) => {
    setForm({
      name:          client.name || "",
      contactPerson: client.contactPerson || "",
      email:         client.email || "",
      phone:         client.phone || "",
      notes:         client.notes || "",
    });
    setEditId(client.id);
    setError(""); setSuccess("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (client) => {
    const count = projectCountByClient[client.id] || 0;
    const msg = count
      ? `Delete client "${client.name}"? ${count} project(s) are linked to it — they will become Unassigned (their data is NOT deleted).`
      : `Delete client "${client.name}"?`;
    if (!window.confirm(msg)) return;
    setError(""); setSuccess("");
    try {
      await deleteDoc(doc(db, "clients", client.id));
      setSuccess(`Client "${client.name}" deleted.`);
    } catch (err) {
      setError(err.message || "Failed to delete client.");
    }
  };

  const handleCancel = () => { setForm(EMPTY_FORM); setEditId(null); setError(""); setSuccess(""); };

  // ── Assign a project to a client (writes clientId onto the project) ──────
  const assignProject = async (projectId, clientId) => {
    setError(""); setSuccess("");
    try {
      await setDoc(
        doc(db, "projects", projectId),
        { clientId: clientId || null },
        { merge: true }
      );
    } catch (err) {
      setError(err.message || "Failed to assign project.");
    }
  };

  // styles (match UserManagement)
  const card  = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: "20px 24px", boxShadow: "0 4px 16px rgba(0,0,0,0.06)" };
  const label = { display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" };
  const input = { width: "100%", border: "1px solid #e2e8f0", borderRadius: 8, padding: "9px 12px", fontSize: 14, color: "#0f172a", outline: "none", boxSizing: "border-box", background: "#f8fafc" };

  return (
    <section style={{ padding: "28px 32px", maxWidth: 1000, margin: "0 auto", fontFamily: "Inter, sans-serif" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 800, color: "#0f172a" }}>Client Management</h2>
      <p style={{ margin: "0 0 28px", color: "#64748b", fontSize: 14 }}>
        Create clients and assign your projects to them. Project access in User Management
        is grouped by client, so assigning projects here keeps everything organised as you scale.
      </p>

      {/* ── Add / Edit Client ─────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 24 }}>
        <h3 style={{ margin: "0 0 18px", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
          {editId ? "Edit Client" : "Add Client"}
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <label style={label}>Client Name *</label>
            <input style={input} placeholder="e.g. Northbridge Municipality"
              value={form.name} onChange={(e) => patch("name", e.target.value)} />
          </div>
          <div>
            <label style={label}>Contact Person</label>
            <input style={input} placeholder="e.g. Jane Smith"
              value={form.contactPerson} onChange={(e) => patch("contactPerson", e.target.value)} />
          </div>
          <div>
            <label style={label}>Email</label>
            <input style={input} type="email" placeholder="contact@client.com"
              value={form.email} onChange={(e) => patch("email", e.target.value)} />
          </div>
          <div>
            <label style={label}>Phone</label>
            <input style={input} placeholder="+27 ..."
              value={form.phone} onChange={(e) => patch("phone", e.target.value)} />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={label}>Notes</label>
            <input style={input} placeholder="Optional"
              value={form.notes} onChange={(e) => patch("notes", e.target.value)} />
          </div>
        </div>

        {error   && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {success && <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", color: "#16a34a", fontSize: 13, marginBottom: 12 }}>{success}</div>}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : (editId ? "Update Client" : "Add Client")}
          </button>
          {editId && (
            <button onClick={handleCancel}
              style={{ background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* ── Client Register ───────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: 24 }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
          Clients ({clients.length})
        </h3>
        {clients.length === 0 ? (
          <p style={{ color: "#94a3b8", fontSize: 14, textAlign: "center", padding: "24px 0" }}>No clients yet. Add your first client above.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                  {["Client", "Contact", "Email", "Projects", "Actions"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id} style={{ borderBottom: "1px solid #f8fafc" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: "#0f172a" }}>{c.name}</td>
                    <td style={{ padding: "10px 12px", color: "#475569" }}>{c.contactPerson || "—"}</td>
                    <td style={{ padding: "10px 12px", color: "#475569" }}>{c.email || "—"}</td>
                    <td style={{ padding: "10px 12px", color: "#475569" }}>{projectCountByClient[c.id] || 0}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => handleEdit(c)}
                          style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Edit</button>
                        <button onClick={() => handleDelete(c)}
                          style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Assign Projects to Clients ────────────────────────────────────── */}
      <div style={card}>
        <h3 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
          Assign Projects to Clients
        </h3>
        <p style={{ margin: "0 0 16px", fontSize: 12, color: "#94a3b8" }}>
          {unassignedCount > 0
            ? `${unassignedCount} project(s) currently unassigned.`
            : "All projects are assigned to a client."}
        </p>
        {projects.length === 0 ? (
          <p style={{ color: "#94a3b8", fontSize: 14, textAlign: "center", padding: "24px 0" }}>No projects yet.</p>
        ) : (
          projects.map((p) => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", border: "1px solid #f1f5f9", borderRadius: 8, marginBottom: 8, background: p.clientId ? "#fff" : "#fffbeb" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{p.name || p.id}</div>
                {p.client && !p.clientId && (
                  <div style={{ fontSize: 11, color: "#b45309" }}>Legacy text client: "{p.client}" — pick a client below to formalise it</div>
                )}
              </div>
              <select
                style={{ ...input, width: 240, padding: "7px 10px", fontSize: 13, cursor: "pointer", background: "#fff" }}
                value={p.clientId || ""}
                onChange={(e) => assignProject(p.id, e.target.value)}
              >
                <option value="">— Unassigned —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          ))
        )}
      </div>
    </section>
  );
}